import React, { useState, useEffect } from 'react';
import { 
  Zap, Plus, Edit2, Trash2, Save, X, Search, 
  Tag, CheckCircle2, Clock, AlertTriangle, ChevronRight,
  Wrench, Home, Shield, Heart, DollarSign, FileSpreadsheet, Database, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// ═══════════════════════════════════════════════════════════════
// ÉLÉMENTS DE DÉCLENCHEMENT
// Interface de gestion des éléments qui déclenchent des ventes
// ═══════════════════════════════════════════════════════════════

const ElementsDeclenchement = () => {
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingElement, setEditingElement] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('tous');

  // Catégories d'éléments de déclenchement
  const CATEGORIES = [
    { id: 'tous', label: 'Tous', icon: Tag, color: 'slate' },
    { id: 'travaux_hors_mpa', label: 'Travaux Hors MPA', icon: Wrench, color: 'purple' },
    { id: 'autres_aides_anah', label: 'Autres Aides ANAH', icon: Home, color: 'cyan' },
    { id: 'conditions_medicales', label: 'Conditions Médicales', icon: Heart, color: 'red' },
    { id: 'situations_speciales', label: 'Situations Spéciales', icon: Shield, color: 'amber' },
    { id: 'opportunites_commerciales', label: 'Opportunités Commerciales', icon: DollarSign, color: 'emerald' }
  ];

  // Éléments par défaut (initial)
  const DEFAULT_ELEMENTS = [
    {
      id: 'sdb_hors_mpa',
      titre: 'Salle de bain hors MPA',
      description: 'Le client n\'est pas éligible MPA mais souhaite faire des travaux de salle de bain sans subvention',
      categorie: 'travaux_hors_mpa',
      declencheurs: ['Non éligible MPA', 'Intéressé par SDB', 'Budget personnel'],
      actions_suggerees: ['Proposer devis SDB sans aide', 'Expliquer les options de financement'],
      priorite: 'haute',
      actif: true
    },
    {
      id: 'pac_maison',
      titre: 'Pompe à chaleur - Maison',
      description: 'Le client possède une maison et peut bénéficier des aides ANAH pour une PAC',
      categorie: 'autres_aides_anah',
      declencheurs: ['Type de bien: Maison', 'Éligible ANAH', 'Chauffage vétuste'],
      actions_suggerees: ['Présenter les aides PAC', 'Calculer les économies potentielles'],
      priorite: 'moyenne',
      actif: true
    },
    {
      id: 'isolation_combles',
      titre: 'Isolation des combles',
      description: 'Opportunité d\'isolation avec aides ANAH pour les propriétaires de maison',
      categorie: 'autres_aides_anah',
      declencheurs: ['Type de bien: Maison', 'Factures énergie élevées', 'Combles non isolés'],
      actions_suggerees: ['Audit énergétique', 'Présenter Réno Ampleur'],
      priorite: 'moyenne',
      actif: true
    },
    {
      id: 'monte_escalier_hors',
      titre: 'Monte-escalier hors MPA',
      description: 'Client non éligible MPA mais ayant besoin d\'un monte-escalier pour mobilité',
      categorie: 'travaux_hors_mpa',
      declencheurs: ['Non éligible MPA', 'Problèmes de mobilité', 'Escaliers dans le logement'],
      actions_suggerees: ['Proposer devis monte-escalier', 'Options de location/achat'],
      priorite: 'haute',
      actif: true
    },
    {
      id: 'eligibilite_conditionnelle',
      titre: 'Éligibilité conditionnelle',
      description: 'Le client a déjà bénéficié de MPA mais ne connaît pas le montant exact',
      categorie: 'situations_speciales',
      declencheurs: ['Déjà bénéficié MPA', 'Montant inconnu', 'Documents manquants'],
      actions_suggerees: ['Demander relevé ANAH', 'Planifier rappel après vérification'],
      priorite: 'haute',
      actif: true
    },
    {
      id: 'gir_mdph_potentiel',
      titre: 'GIR/MDPH potentiel',
      description: 'Client 60-69 ans sans attestation mais situation médicale suggère éligibilité',
      categorie: 'conditions_medicales',
      declencheurs: ['Âge 60-69 ans', 'Difficultés de mobilité', 'Pas de GIR/MDPH'],
      actions_suggerees: ['Orienter vers médecin traitant', 'Expliquer démarches MDPH'],
      priorite: 'moyenne',
      actif: true
    }
  ];

  // Charger les éléments
  useEffect(() => {
    loadElements();
  }, []);

  const loadElements = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/api/declenchements/elements`);
      if (response.ok) {
        const data = await response.json();
        setElements(data.elements?.length > 0 ? data.elements : DEFAULT_ELEMENTS);
      } else {
        // Si pas de données, utiliser les défauts
        setElements(DEFAULT_ELEMENTS);
      }
    } catch (error) {
      console.error('Erreur chargement éléments:', error);
      setElements(DEFAULT_ELEMENTS);
    }
    setLoading(false);
  };

  const saveElements = async (newElements) => {
    try {
      await authFetch(`${API}/api/declenchements/elements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ elements: newElements })
      });
    } catch (error) {
      console.error('Erreur sauvegarde éléments:', error);
    }
  };

  // Filtrer les éléments
  const filteredElements = elements.filter(el => {
    const matchSearch = el.titre.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       el.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCategory = selectedCategory === 'tous' || el.categorie === selectedCategory;
    return matchSearch && matchCategory;
  });

  // Couleurs de priorité
  const priorityColors = {
    haute: 'text-red-400 bg-red-500/10 border-red-500/30',
    moyenne: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    basse: 'text-slate-400 bg-slate-500/10 border-slate-500/30'
  };

  // Ajouter/Modifier un élément
  const handleSaveElement = async (element) => {
    let newElements;
    if (editingElement) {
      newElements = elements.map(el => el.id === editingElement.id ? element : el);
    } else {
      element.id = `elem_${Date.now()}`;
      newElements = [...elements, element];
    }
    setElements(newElements);
    await saveElements(newElements);
    setShowAddModal(false);
    setEditingElement(null);
    toast.success(editingElement ? 'Élément modifié' : 'Élément ajouté');
  };

  // Supprimer un élément
  const handleDeleteElement = async (id) => {
    if (!window.confirm('Supprimer cet élément de déclenchement ?')) return;
    const newElements = elements.filter(el => el.id !== id);
    setElements(newElements);
    await saveElements(newElements);
    toast.success('Élément supprimé');
  };

  // Toggle actif/inactif
  const toggleActif = async (id) => {
    const newElements = elements.map(el => 
      el.id === id ? { ...el, actif: !el.actif } : el
    );
    setElements(newElements);
    await saveElements(newElements);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Zap className="w-7 h-7 text-amber-500" />
            Éléments de Déclenchement
          </h2>
          <p className="text-slate-400 mt-1">
            Configurez les situations qui déclenchent des opportunités de vente
          </p>
        </div>
        <button
          onClick={() => { setEditingElement(null); setShowAddModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-lg transition-colors"
        >
          <Plus size={18} />
          Ajouter un élément
        </button>
      </div>


      {/* Filtres */}
      <div className="flex flex-wrap gap-4">
        {/* Recherche */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un élément..."
            className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none"
          />
        </div>

        {/* Catégories */}
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  selectedCategory === cat.id
                    ? `bg-${cat.color}-500/20 text-${cat.color}-400 border border-${cat.color}-500/30`
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                <Icon size={16} />
                <span className="text-sm">{cat.label}</span>
                {cat.id !== 'tous' && (
                  <span className="text-xs bg-slate-700 px-1.5 py-0.5 rounded">
                    {elements.filter(el => el.categorie === cat.id).length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Liste des éléments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredElements.map(element => {
          const category = CATEGORIES.find(c => c.id === element.categorie);
          const CategoryIcon = category?.icon || Tag;
          
          return (
            <div
              key={element.id}
              className={`p-4 rounded-xl border transition-all ${
                element.actif 
                  ? 'bg-slate-900/50 border-slate-700 hover:border-slate-600'
                  : 'bg-slate-900/30 border-slate-800 opacity-60'
              }`}
            >
              {/* Header de la carte */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-${category?.color || 'slate'}-500/20`}>
                    <CategoryIcon className={`w-5 h-5 text-${category?.color || 'slate'}-400`} />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">{element.titre}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded border ${priorityColors[element.priorite]}`}>
                      Priorité {element.priorite}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleActif(element.id)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      element.actif ? 'text-emerald-400 hover:bg-emerald-500/20' : 'text-slate-500 hover:bg-slate-700'
                    }`}
                    title={element.actif ? 'Désactiver' : 'Activer'}
                  >
                    {element.actif ? <CheckCircle2 size={18} /> : <Clock size={18} />}
                  </button>
                  <button
                    onClick={() => { setEditingElement(element); setShowAddModal(true); }}
                    className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDeleteElement(element.id)}
                    className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-slate-400 mb-3">{element.description}</p>

              {/* Déclencheurs */}
              <div className="mb-3">
                <span className="text-xs text-slate-500 uppercase tracking-wide">Déclencheurs:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {element.declencheurs.map((dec, i) => (
                    <span key={i} className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">
                      {dec}
                    </span>
                  ))}
                </div>
              </div>

              {/* Actions suggérées */}
              <div>
                <span className="text-xs text-slate-500 uppercase tracking-wide">Actions suggérées:</span>
                <div className="mt-1 space-y-1">
                  {element.actions_suggerees.map((action, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-400">
                      <ChevronRight size={12} className="text-amber-500" />
                      {action}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredElements.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <Zap className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Aucun élément trouvé</p>
        </div>
      )}

      {/* Modal Ajouter/Modifier */}
      {showAddModal && (
        <ElementModal
          element={editingElement}
          categories={CATEGORIES.filter(c => c.id !== 'tous')}
          onSave={handleSaveElement}
          onClose={() => { setShowAddModal(false); setEditingElement(null); }}
        />
      )}
    </div>
  );
};

// Modal pour ajouter/modifier un élément
const ElementModal = ({ element, categories, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    titre: element?.titre || '',
    description: element?.description || '',
    categorie: element?.categorie || 'travaux_hors_mpa',
    declencheurs: element?.declencheurs || [''],
    actions_suggerees: element?.actions_suggerees || [''],
    priorite: element?.priorite || 'moyenne',
    actif: element?.actif ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.titre.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    onSave({
      ...formData,
      id: element?.id,
      declencheurs: formData.declencheurs.filter(d => d.trim()),
      actions_suggerees: formData.actions_suggerees.filter(a => a.trim())
    });
  };

  const addDeclencheur = () => {
    setFormData(prev => ({ ...prev, declencheurs: [...prev.declencheurs, ''] }));
  };

  const addAction = () => {
    setFormData(prev => ({ ...prev, actions_suggerees: [...prev.actions_suggerees, ''] }));
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">
            {element ? 'Modifier l\'élément' : 'Nouvel élément de déclenchement'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Titre */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Titre *</label>
            <input
              type="text"
              value={formData.titre}
              onChange={e => setFormData(prev => ({ ...prev, titre: e.target.value }))}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
              placeholder="Ex: Salle de bain hors MPA"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
              placeholder="Description de la situation..."
            />
          </div>

          {/* Catégorie et Priorité */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Catégorie</label>
              <select
                value={formData.categorie}
                onChange={e => setFormData(prev => ({ ...prev, categorie: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Priorité</label>
              <select
                value={formData.priorite}
                onChange={e => setFormData(prev => ({ ...prev, priorite: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
              >
                <option value="haute">Haute</option>
                <option value="moyenne">Moyenne</option>
                <option value="basse">Basse</option>
              </select>
            </div>
          </div>

          {/* Déclencheurs */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Déclencheurs</label>
            {formData.declencheurs.map((dec, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={dec}
                  onChange={e => {
                    const newDec = [...formData.declencheurs];
                    newDec[i] = e.target.value;
                    setFormData(prev => ({ ...prev, declencheurs: newDec }));
                  }}
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
                  placeholder="Ex: Non éligible MPA"
                />
                {formData.declencheurs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      const newDec = formData.declencheurs.filter((_, j) => j !== i);
                      setFormData(prev => ({ ...prev, declencheurs: newDec }));
                    }}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addDeclencheur}
              className="text-sm text-amber-400 hover:text-amber-300"
            >
              + Ajouter un déclencheur
            </button>
          </div>

          {/* Actions suggérées */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Actions suggérées</label>
            {formData.actions_suggerees.map((action, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={action}
                  onChange={e => {
                    const newActions = [...formData.actions_suggerees];
                    newActions[i] = e.target.value;
                    setFormData(prev => ({ ...prev, actions_suggerees: newActions }));
                  }}
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
                  placeholder="Ex: Proposer devis SDB"
                />
                {formData.actions_suggerees.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      const newActions = formData.actions_suggerees.filter((_, j) => j !== i);
                      setFormData(prev => ({ ...prev, actions_suggerees: newActions }));
                    }}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addAction}
              className="text-sm text-amber-400 hover:text-amber-300"
            >
              + Ajouter une action
            </button>
          </div>

          {/* Boutons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-lg transition-colors"
            >
              <Save size={18} />
              {element ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ElementsDeclenchement;
